import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function CaseStudiesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Success Stories</h1>
          <p className="text-xl md:text-2xl max-w-3xl font-light">
            Real results for real businesses. Discover how we've helped our clients achieve measurable growth and transformational success.
          </p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Client Success Stories</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our track record speaks for itself. Here are some examples of how we've helped businesses overcome challenges and achieve their strategic objectives.
            </p>
          </div>

          <div className="space-y-20">
            {/* Case Study 1 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/card_400_x_480/public/content-type-blog/2025-12/sector_trends_-_tmt.jpg.webp?itok=wUEDCv4J"
                    alt="Technology Case Study"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-computer-line"></i>
                  <span className="font-semibold">Technology</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">SaaS Revenue Growth Strategy</h3>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className="text-4xl font-bold text-teal-600">150%</div>
                    <div className="text-sm text-gray-600">Revenue Increase</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">3x</div>
                    <div className="text-sm text-gray-600">Customer Growth</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">18mo</div>
                    <div className="text-sm text-gray-600">Timeline</div>
                  </div>
                </div>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  A mid-sized SaaS company was struggling with pricing strategy and market positioning. We conducted comprehensive market analysis, redesigned their pricing model, and developed a go-to-market strategy that resulted in significant revenue growth and market share expansion.
                </p>
                <div className="space-y-3">
                  <h4 className="font-bold text-gray-900">Key Outcomes:</h4>
                  <ul className="space-y-2">
                    {[
                      'Optimized pricing tiers increased average revenue per user by 45%',
                      'New market segmentation strategy expanded addressable market',
                      'Sales enablement program improved conversion rates by 60%',
                      'Customer retention improved from 82% to 94%'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Case Study 2 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-heart-pulse-line"></i>
                  <span className="font-semibold">Healthcare</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Healthcare Operational Excellence</h3>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className="text-4xl font-bold text-teal-600">40%</div>
                    <div className="text-sm text-gray-600">Cost Reduction</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">25%</div>
                    <div className="text-sm text-gray-600">Efficiency Gain</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">12mo</div>
                    <div className="text-sm text-gray-600">Timeline</div>
                  </div>
                </div>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  A regional healthcare provider faced rising operational costs and declining patient satisfaction. We implemented a comprehensive operational improvement program that streamlined processes, reduced waste, and enhanced patient care quality.
                </p>
                <div className="space-y-3">
                  <h4 className="font-bold text-gray-900">Key Outcomes:</h4>
                  <ul className="space-y-2">
                    {[
                      'Process optimization reduced patient wait times by 35%',
                      'Supply chain improvements cut procurement costs by $2.4M annually',
                      'Staff productivity increased through workflow redesign',
                      'Patient satisfaction scores improved from 72% to 89%'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/card_400_x_480/public/content-type-blog/2025-12/gettyimages_goran13_2184632666_hd.jpg.webp?itok=CdJbtK60"
                    alt="Healthcare Case Study"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Case Study 3 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/card_400_x_480/public/content-type-blog/2025-12/sector_trends_-_ind.jpg.webp?itok=td7KET0Y"
                    alt="Manufacturing Case Study"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-factory-line"></i>
                  <span className="font-semibold">Manufacturing</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Global Market Expansion</h3>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className="text-4xl font-bold text-teal-600">5</div>
                    <div className="text-sm text-gray-600">New Markets</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">$12M</div>
                    <div className="text-sm text-gray-600">New Revenue</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">24mo</div>
                    <div className="text-sm text-gray-600">Timeline</div>
                  </div>
                </div>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  A manufacturing company wanted to expand beyond their domestic market but lacked international experience. We developed and executed a comprehensive market entry strategy that successfully established their presence in five new countries.
                </p>
                <div className="space-y-3">
                  <h4 className="font-bold text-gray-900">Key Outcomes:</h4>
                  <ul className="space-y-2">
                    {[
                      'Market analysis identified optimal entry markets and strategies',
                      'Partnership development secured local distribution channels',
                      'Product adaptation ensured market fit and regulatory compliance',
                      'Sales team training enabled successful market penetration'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Case Study 4 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-bank-line"></i>
                  <span className="font-semibold">Finance</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Digital Transformation Initiative</h3>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className="text-4xl font-bold text-teal-600">65%</div>
                    <div className="text-sm text-gray-600">Digital Adoption</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">30%</div>
                    <div className="text-sm text-gray-600">Cost Savings</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">15mo</div>
                    <div className="text-sm text-gray-600">Timeline</div>
                  </div>
                </div>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  A traditional financial institution needed to modernize their operations and customer experience to compete with digital-first competitors. We led a comprehensive digital transformation that revolutionized their business model.
                </p>
                <div className="space-y-3">
                  <h4 className="font-bold text-gray-900">Key Outcomes:</h4>
                  <ul className="space-y-2">
                    {[
                      'Mobile banking platform increased customer engagement by 80%',
                      'Automated processes reduced operational costs significantly',
                      'Data analytics capabilities improved decision-making',
                      'Customer satisfaction scores reached all-time highs'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/card_400_x_480/public/content-type-blog/2025-12/gettyimages_brothers91_2211153615_hd.jpg.webp?itok=EXFiLl8E"
                    alt="Finance Case Study"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Case Study 5 */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://www.simon-kucher.com/sites/default/files/styles/card_400_x_480/public/content-type-blog/2025-12/sector_trends_-_con.jpg.webp?itok=86d8yFbE"
                    alt="Professional Services Case Study"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                  <i className="ri-briefcase-line"></i>
                  <span className="font-semibold">Professional Services</span>
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Practice Growth & Profitability</h3>
                <div className="flex items-center gap-6 mb-6">
                  <div>
                    <div className="text-4xl font-bold text-teal-600">85%</div>
                    <div className="text-sm text-gray-600">Revenue Growth</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">50%</div>
                    <div className="text-sm text-gray-600">Margin Improvement</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-600">20mo</div>
                    <div className="text-sm text-gray-600">Timeline</div>
                  </div>
                </div>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  A mid-sized professional services firm was experiencing stagnant growth and declining profitability. We implemented a comprehensive practice transformation that repositioned their services and optimized operations.
                </p>
                <div className="space-y-3">
                  <h4 className="font-bold text-gray-900">Key Outcomes:</h4>
                  <ul className="space-y-2">
                    {[
                      'Service portfolio optimization focused on high-value offerings',
                      'Pricing strategy overhaul increased average project value by 40%',
                      'Business development program generated consistent new client pipeline',
                      'Operational efficiency improvements boosted utilization rates'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "The team's strategic insights and hands-on approach were instrumental in our successful market expansion. They truly became an extension of our leadership team.",
                author: "Jennifer Lee",
                role: "CEO, Manufacturing Company",
                rating: 5
              },
              {
                quote: "Their expertise in operational improvement helped us achieve cost savings we didn't think were possible while actually improving service quality.",
                author: "Dr. Robert Chen",
                role: "COO, Healthcare Provider",
                rating: 5
              },
              {
                quote: "Working with this consulting firm transformed our business. Their pricing strategy recommendations alone paid for the engagement multiple times over.",
                author: "Sarah Mitchell",
                role: "Founder, SaaS Company",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Write Your Success Story?</h2>
          <p className="text-xl mb-8 text-gray-300">
            Let's discuss how we can help you achieve similar results for your business.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
          >
            Schedule a Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
